import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BROKER_COMPARISON_BATCH_SIZE, GALLAGHER_BROKER_TENANT_ID, equalBrokerComparisonCapabilities,
  getBrokerLimitComparisons, validBrokerComparisonId,
} from '../api/brokerLimitComparisonApi';

export const brokerLimitComparisonsEnabled = ({ tenantId, insuranceEnabled, isDistributor, requesterId, identityReady = false }) =>
  identityReady === true && tenantId === GALLAGHER_BROKER_TENANT_ID && insuranceEnabled === true && isDistributor === true && validBrokerComparisonId(requesterId);

// This facts-only path never loads or persists DCL assessments or policy details.
export default function useBrokerLimitComparisons({ tenantId, insuranceEnabled, isDistributor, requesterId, identityReady = false, companyIds = [], revision }) {
  const enabled = brokerLimitComparisonsEnabled({ tenantId, insuranceEnabled, isDistributor, requesterId, identityReady });
  const validIds = Array.isArray(companyIds) && companyIds.every(validBrokerComparisonId);
  const idsKey = validIds ? JSON.stringify([...new Set(companyIds)].sort()) : null;
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const scope = useMemo(() => ({ key: String(++generation.current), tenantId, requesterId, enabled, idsKey, revision, retry }),
    [tenantId, requesterId, enabled, idsKey, revision, retry]);
  const [state, setState] = useState({ scope: null, comparisons: {}, capabilities: null, loading: false, error: '' });
  const reload = useCallback(() => setRetry(value => value + 1), []);
  useEffect(() => {
    if (!enabled) return undefined;
    const ids = idsKey === null ? null : JSON.parse(idsKey);
    if (!ids || ids.length === 0) {
      setState({ scope, comparisons: {}, capabilities: null, loading: false, error: ids ? '' : 'The company scope could not be verified.' });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    setState({ scope, comparisons: {}, capabilities: null, loading: true, error: '' });
    const load = async () => {
      const comparisons = Object.create(null);
      let capabilities = null;
      try {
        for (let offset = 0; offset < ids.length; offset += BROKER_COMPARISON_BATCH_SIZE) {
          const result = await getBrokerLimitComparisons(tenantId, ids.slice(offset, offset + BROKER_COMPARISON_BATCH_SIZE), {
            requesterId, signal: controller.signal,
          });
          if (cancelled || controller.signal.aborted) return;
          if (capabilities && !equalBrokerComparisonCapabilities(capabilities, result.capabilities)) {
            throw new Error('Insurer limit permissions changed. Reload insurer limits.');
          }
          capabilities = result.capabilities;
          for (const row of result.comparisons) comparisons[row.companyId] = row;
        }
        if (!cancelled) setState({ scope, comparisons, capabilities, loading: false, error: '' });
      } catch (error) {
        if (!cancelled) setState({ scope, comparisons: {}, capabilities: null, loading: false,
          error: error?.message || 'Insurer limits are currently unavailable. Reload insurer limits.' });
      }
    };
    load();
    return () => { cancelled = true; controller.abort(); };
  }, [enabled, idsKey, tenantId, requesterId, scope]);
  // Mask the old capture during render, before effect cleanup or a new request completes.
  const visible = enabled && state.scope === scope ? state : {
    comparisons: {}, capabilities: null, loading: enabled && idsKey !== null && idsKey !== '[]', error: '',
  };
  return { enabled, comparisons: visible.comparisons, capabilities: visible.capabilities, loading: visible.loading,
    error: visible.error, reload, scopeKey: scope.key };
}
